<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedGroups"
      multi-select
      searchable
      class="tw-min-w-[150px]"
      data-test="tc-filter-business-setup"
      :list="businessSetupOptions"
      :label="meta.filter.quickFilter ? '' : $t('Business setup')"
      :placeholder="$t('All')"
      :loading="loading"
      can-deselect
      labels-primary
    />
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack } from '@worksome/wds-components'
import { computed, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { $t } from '@/composables/translation'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'

defineProps<{ meta: FilterMetaDataProp }>()

const selectedBusinessSetup: ModelRef<{ label: string; value: string }[]> = defineModel({
  default: () => [],
})

const TrustedContactBusinessSetupsQuery = gql`
  query TrustedContactBusinessSetupsFilterQuery {
    trustedContactBusinessSetups {
      label
      values
    }
  }
`

const { result, loading } = useQuery(TrustedContactBusinessSetupsQuery, null, {
  clientId: 'publicApi',
  fetchPolicy: 'cache-and-network',
})

type BusinessSetupGroup = { label: string; value: string; values: string[] }

// The same setup (e.g. "Limited company") exists once per market, so the API groups them by
// name. Each option is one group, keyed by its label, covering every market's enum value.
const businessSetupOptions = computed<BusinessSetupGroup[]>(() => {
  const groups =
    (result.value?.trustedContactBusinessSetups as { label: string; values: string[] }[]) ?? []

  return groups
    .map(({ label, values }) => ({ label, value: label, values }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

// The table sends each selected item's `value` as a BusinessEntityType, so the model holds
// the individual enum values while the select works with the groups.
const selectedGroups = computed<BusinessSetupGroup[]>({
  get: () => {
    const selected = new Set(selectedBusinessSetup.value.map(({ value }) => value))

    return businessSetupOptions.value.filter(({ values }) => values.some((v) => selected.has(v)))
  },
  set: (groups) => {
    selectedBusinessSetup.value = groups.flatMap(({ label, values }) =>
      values.map((value) => ({ label, value })),
    )
  },
})
</script>
